import React, { useState } from 'react';
import { getImagePathByMarketKey } from '../../../services/bet-finder-service';

interface BookLogoProps {
  bookKey: string;
  title: string;
  /** Class for the outer chip that frames the logo (or fallback). */
  chipClassName: string;
  /** Class for the <img> itself when a real logo loads successfully. */
  imgClassName: string;
}

/**
 * Renders a bookmaker's logo inside a neutral chip, always falling back to
 * the book's initials when the source image is missing or fails to load
 * (several of our hot-linked logo URLs 404 / get blocked by hotlink
 * protection). Without this, a broken image just leaves an empty chip,
 * which reads as a missing logo rather than a deliberate icon set. The
 * chip also keeps every book's brand colors contained at a consistent
 * size instead of a strip of clashing raw logo art.
 */
const BookLogo: React.FC<BookLogoProps> = ({ bookKey, title, chipClassName, imgClassName }) => {
  const src = getImagePathByMarketKey(bookKey);
  const [failed, setFailed] = useState(false);
  const showFallback = !src || failed;

  const initials =
    (title || bookKey)
      .replace(/\.(ag|com|au)$/i, '')
      .split(/[\s-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || '?';

  return (
    <span className={chipClassName}>
      {showFallback ? (
        <span className="book-logo__fallback">{initials}</span>
      ) : (
        <img
          src={src}
          alt=""
          className={imgClassName}
          onError={() => setFailed(true)}
        />
      )}
    </span>
  );
};

export default BookLogo;
